'use client';

/**
 * DetailsPanel - Schema-driven entity detail view
 *
 * Renders entity details based on ENTITY_SCHEMA configuration.
 * Replaces entity-specific components (ProjectDetails, RFQDetails, etc.)
 * with a single, schema-driven component.
 *
 * @component_role Detail View
 * @business_rule Renders sections based on schema config for any entity type
 * @integration_point Uses ENTITY_SCHEMA from total-matrix store
 *
 * @layer presentation
 * @arch_component_type widget
 * @arch_domain copilot
 */

import { useEntitySchema } from '@/lib/stores/total-matrix-store';
import { SectionRenderer } from './section-renderer';
import { LoadingState } from './shared';

interface DetailsPanelProps {
  entityType: string;
  entityId: string;
  data: Record<string, unknown>;
  isLoading?: boolean;
  // Click-to-focus on an `items` section's line rows — OFF by default,
  // so mode1's existing `mcp_details` rail card (card-registry.tsx)
  // renders exactly as it does today. The Cost Plan rail surface
  // (mode3/mode11, cost-plan-surface.tsx) is the only caller that
  // passes `interactive`, via EntityDetailsCard.
  interactive?: boolean;
  focusedSourceLineSid?: string | null;
  onItemClick?: (item: Record<string, unknown>) => void;
}

export function DetailsPanel({
  entityType,
  entityId,
  data,
  isLoading = false,
  interactive = false,
  focusedSourceLineSid = null,
  onItemClick,
}: DetailsPanelProps) {
  const schema = useEntitySchema(entityType);

  // Schema not loaded yet
  if (!schema) {
    return <LoadingState message="Loading schema..." />;
  }

  // Show loading state while data is being fetched
  if (isLoading) {
    return (
      <div className="space-y-4">
        {schema.sections.map((section, idx) => (
          <SectionRenderer
            key={idx}
            config={section}
            data={{}}
            entityType={entityType}
            entityId={entityId}
            isLoading={true}
          />
        ))}
      </div>
    );
  }

  // No data available
  if (!data || Object.keys(data).length === 0) {
    return (
      <div className="text-sm text-muted-foreground">
        No details available
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {schema.sections.map((section, idx) => (
        <SectionRenderer
          key={idx}
          config={section}
          data={data}
          entityType={entityType}
          entityId={entityId}
          interactive={interactive}
          focusedSourceLineSid={focusedSourceLineSid}
          onItemClick={onItemClick}
        />
      ))}
    </div>
  );
}
